import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { Checkbox, Dropdown, Icon } from 'semantic-ui-react'
import { difference } from 'lodash'
import ReactGA from 'react-ga4'

import { CardType, defaultColorLabel, EDH_FORMATS, FormatType, getCategoryName } from 'types/deck'
import { Color } from 'services/apiTypes/card.types'

import { store } from 'redux/store'
import { selectCommanderCards } from 'redux/deck/selectors'
import { useAppSelector } from 'redux/hooks'

import { DeckListResponse, EstimatedBudget } from 'services/apiTypes/deck.types'
import { packageTabKey } from 'services/accountSettings.service'

import SaveService from 'services/save.service'
import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import PackageListItem from 'components/deckPage/modals/cardPackages/ListItem'
import Accordion from 'components/elements/Accordion'
import LineLoader from 'components/elements/LineLoader'
import Input from 'components/formElements/SemanticInput'
import FormatDropdown from 'components/cardSearchPanel/searchFormElements/formatDropdown'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import TabContainer from 'components/elements/TabButtons'

import { getQueryParamsBySettings } from 'utils/Search'
import { getCardsColorIdenty } from 'utils/cardHelpers'
import { responseToCard } from 'utils/ResponseToResults'

import styles from './packageSelectorList.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  onSelectPackage: (deckId: number | null) => void
}

const PackageSelectorList = ({ open, onClose, onSelectPackage }: Props) => {
  const [loading, setLoading] = useState(false)
  const [packages, setPackages] = useState<DeckListResponse>()
  const [searchValue, setSearchValue] = useState('')

  const [orderBy, setOrderBy] = useState('-importCount')
  const [intendedFormat, setIntendedFormat] = useState<FormatType>()
  const [maxBudget, setMaxBudget] = useState<EstimatedBudget>(0)

  const format = useAppSelector(state => state.deck.format)
  const colorLabels = useAppSelector(state => state.deck.colorLabels)

  const [importIntoCategory, setImportIntoCategory] = useState<'maintain' | 'auto' | 'maybeboard'>('maintain')
  const [addOnlyColorIdentOfCommander, setAddOnlyColorIdentOfCommander] = useState(true)
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [importIntoColorTag, setImportIntoColorTag] = useState(colorLabels[0]?.name || '')
  const [forceQuantityToOne, setForceQuantityToOne] = useState(EDH_FORMATS.includes(format))

  const [{ tbUser: username, [packageTabKey]: selectedTab = 'default' }, setCookies] = useCookies([
    'tbUser',
    packageTabKey,
  ])

  useEffect(() => {
    if (!open) return

    setSearchValue('')
    handleFetchPackages()
  }, [open, selectedTab])

  const handleFetchPackages = (queryString = '') => {
    setLoading(true)
    setPackages({ results: [], count: 0, next: '' })

    const params = getQueryParamsBySettings(selectedTab, queryString, username, orderBy, intendedFormat, maxBudget)

    return DeckService.listV3(params)
      .then(res => {
        if (selectedTab !== 'followed') return setPackages(res)

        setPackages({
          ...res,
          results: res.results.map(deck => ({ ...deck, bookmarked: true })),
        })
      })
      .catch(err => ToastService.create(err, 'Card packages'))
      .finally(() => setLoading(false))
  }

  const handleAddCardsToDeck = (deckId: number) => {
    if (!deckId) return

    setLoading(true)

    DeckService.details(deckId, true)
      .then(res => {
        const state = store.getState()

        // prettier-ignore
        const cardsInDeck: Record<string, boolean> = Object.values(state.deck.cardMap).reduce((acc, card) => ({ ...acc, [card.oracleCardId]: true  }), {})
        const commanders = selectCommanderCards(state)
        const commandersColorIdent = commanders.reduce<Color[]>(getCardsColorIdenty, new Array<Color>())

        const packageCards = res.cards.map(deckCard => ({
          ...responseToCard(deckCard.card, deckCard),
          categories: importIntoCategory === 'auto' ? [] : deckCard.categories, // Clearing categories if not maintaining, allow default category function to take over
          deckRelationId: '', // Force off the deckRelationId to unlink the cards from the package
        }))

        const cards: CardType[] = packageCards
          .filter(card => {
            const colorDifference = difference(card.colorIdentity, commandersColorIdent).length !== 0

            if (skipDuplicates && cardsInDeck[card.oracleCardId]) return false
            if (addOnlyColorIdentOfCommander && commanders.length && colorDifference) return false

            return true
          })
          .map(card => ({
            ...card,
            qty: forceQuantityToOne ? 1 : card.qty,
            colorLabel: colorLabels.find(tag => tag.name === importIntoColorTag) || defaultColorLabel,
            categories:
              importIntoCategory === 'maintain'
                ? card.categories || []
                : importIntoCategory === 'auto'
                  ? [getCategoryName(card)]
                  : ['Maybeboard'],
          }))

        SaveService.save(cards).then(() => {
          ReactGA.event({ category: 'Deck', action: `Added package to existing deck` })
        })
      })
      .catch(() => ToastService.create('Unable to fetch card package', 'Card packages'))
      .finally(() => setLoading(false))
  }

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <div className={styles.header}>
        <h3>
          <Icon name="cube" /> Package Selection
        </h3>
      </div>

      <TabContainer
        className={styles.tabContainer}
        optionsClassName={styles.tabButton}
        options={[
          {
            label: 'Default',
            selected: selectedTab === 'default',
            onClick: () =>
              setCookies(packageTabKey, 'default', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'default' ? styles.activeTab : '',
          },
          {
            label: 'Mine',
            selected: selectedTab === 'mine',
            onClick: () =>
              setCookies(packageTabKey, 'mine', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'mine' ? styles.activeTab : '',
          },
          {
            label: 'Bookmarked',
            selected: selectedTab === 'followed',
            onClick: () =>
              setCookies(packageTabKey, 'followed', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'followed' ? styles.activeTab : '',
          },
          {
            label: 'All',
            selected: selectedTab === 'search',
            onClick: () =>
              setCookies(packageTabKey, 'search', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'search' ? styles.activeTab : '',
          },
        ]}
      />

      <div className={styles.searchFilters}>
        <form
          className={styles.form}
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault()

            handleFetchPackages(searchValue)
          }}>
          <Input
            value={searchValue}
            onChange={setSearchValue}
            className={styles.searchInput}
            placeholder="Mana ramp, etc"
          />
          <button type="submit">
            <Icon name="arrow right" />
          </button>
        </form>

        <Accordion label="Search Options">
          <div className={styles.options}>
            <label>Order card packages by</label>
            <Dropdown
              selection
              onChange={(e, { value }: any) => setOrderBy(value)}
              options={[
                { key: 'importCount', text: 'Times imported', value: '-importCount' },
                { key: 'name', text: 'Package Name', value: 'name' },
                { key: 'updatedAt', text: 'Edited time', value: '-updatedAt' },
              ]}
              value={orderBy}
            />
            <label>Intended format</label>
            <FormatDropdown
              placeholder="Not all packages have intended formats"
              value={intendedFormat}
              onChange={deckFormat => setIntendedFormat(deckFormat)}
            />

            <label>Maximum budget</label>
            <Dropdown
              selection
              placeholder="Choose maximum budget"
              onChange={(e, { value }: any) => setMaxBudget(value)}
              options={[
                { key: 0, text: 'Any', value: 0 },
                { key: 1, text: '$', value: 1 },
                { key: 2, text: '$$', value: 2 },
                { key: 3, text: '$$$', value: 3 },
                { key: 4, text: '$$$$', value: 4 },
              ]}
              value={maxBudget ?? undefined}
            />
          </div>
        </Accordion>

        <Accordion label="Import Options">
          <div className={styles.options}>
            <label>Import category</label>
            <Dropdown
              selection
              onChange={(e, { value }: any) => setImportIntoCategory(value)}
              options={[
                { key: 'maintain', text: 'Carry over categories', value: 'maintain' },
                { key: 'auto', text: 'Default card category', value: 'auto' },
                { key: 'maybeboard', text: 'Maybeboard', value: 'maybeboard' },
              ]}
              value={importIntoCategory}
            />
            <label>Import color tag</label>
            <Dropdown
              selection
              label="Set color label on import"
              onChange={(e, { value }: any) => setImportIntoColorTag(value)}
              options={colorLabels.map(tag => ({
                key: tag.name,
                text: tag.name || 'Default',
                value: tag.name,
              }))}
              value={importIntoColorTag}
            />

            <Checkbox
              onChange={() => setAddOnlyColorIdentOfCommander(!addOnlyColorIdentOfCommander)}
              checked={addOnlyColorIdentOfCommander}
              label="Do not include cards outside commander's color identity"
            />

            <Checkbox
              onChange={() => setSkipDuplicates(!skipDuplicates)}
              checked={skipDuplicates}
              label="Do not include duplicate cards"
            />

            <Checkbox
              onChange={() => setForceQuantityToOne(!forceQuantityToOne)}
              checked={forceQuantityToOne}
              label="Force card quantities to single copy"
            />
          </div>
        </Accordion>
      </div>

      <LineLoader className={`${styles.loader} ${loading ? styles.showLoader : ''}`} />

      <div className={styles.packages}>
        {packages?.results.map(deck => (
          <PackageListItem
            openInNewTab
            key={deck.id}
            package={deck}
            onAddPackage={handleAddCardsToDeck}
            onViewPackageDetails={onSelectPackage}
          />
        ))}
      </div>
    </GlobalOverlayStack>
  )
}

export default PackageSelectorList
